import { ReactNode, createContext, useContext, useMemo } from 'react';

/**
 * Helper function to create a context hook and provider
 *
 * @example ```
 *  const [FooProvider, useFooContext] = createScopedContext<{ foo: string }>();
 * ```
 */
export const createScopedContext = <T extends object>() => {
  const Context = createContext<T | null>(null);

  const useScopedContext = () => {
    const context = useContext(Context);

    if (!context) {
      throw new Error('useScopedContext must be used within a ScopedProvider');
    }

    return context;
  };

  const ScopedProvider = ({
    children,
    ...props
  }: { children: ReactNode } & T) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const memoizedProps = useMemo(() => props, Object.values(props)) as T;

    return (
      <Context.Provider value={memoizedProps}>{children}</Context.Provider>
    );
  };

  return [ScopedProvider, useScopedContext] as const;
};
